/**
 * Lightweight i18n — no external dep. Ships English + Vietnamese.
 * For 100+ keys we'd switch to @solid-primitives/i18n; at this size
 * a flat map is simpler + zero bundle cost.
 */
import { createSignal } from "solid-js";

export type Locale = "en" | "vi";

export const en = {
  "nav.studies":   "Studies",
  "nav.worklist":  "Worklist",
  "nav.reports":   "Reports",
  "nav.admin":     "Admin",

  "study.search":  "Patient name, MRN, accession…",
  "study.refresh": "Refresh",
  "study.empty":   "No studies match the current filter.",

  "viewer.instance":   "instance",
  "viewer.reset":      "Reset",
  "viewer.auto":       "Auto",
  "viewer.ruler":      "Ruler",
  "viewer.clear":      "Clear",
  "viewer.sign":       "Sign report",
  "viewer.presets":    "presets",
  "viewer.hint":       "right-drag = W/L · wheel = zoom · drag = pan",

  "report.title":        "Sign report",
  "report.subtitle":     "Basic Text SR → C-STORE → RIS",
  "report.patient":      "Patient",
  "report.findings":     "Findings",
  "report.discard":      "Discard",
  "report.sign":         "Sign + send",
  "report.sending":      "Signing…",
  "report.ok":           "Report sent",
  "report.fail":         "Failed",

  "auth.intro":   "Sign in with your hospital credentials to continue.",
  "auth.signin":  "Sign in with SSO",
} as const;

export const vi: typeof en = {
  "nav.studies":   "Nghiên cứu",
  "nav.worklist":  "Danh sách",
  "nav.reports":   "Báo cáo",
  "nav.admin":     "Quản trị",

  "study.search":  "Tên bệnh nhân, MRN, số nhập viện…",
  "study.refresh": "Làm mới",
  "study.empty":   "Không có nghiên cứu phù hợp bộ lọc.",

  "viewer.instance":   "lát cắt",
  "viewer.reset":      "Khôi phục",
  "viewer.auto":       "Tự động",
  "viewer.ruler":      "Thước",
  "viewer.clear":      "Xoá",
  "viewer.sign":       "Ký báo cáo",
  "viewer.presets":    "mẫu",
  "viewer.hint":       "chuột phải kéo = W/L · cuộn = thu phóng · kéo = di chuyển",

  "report.title":        "Ký báo cáo",
  "report.subtitle":     "Basic Text SR → C-STORE → RIS",
  "report.patient":      "Bệnh nhân",
  "report.findings":     "Nhận định",
  "report.discard":      "Huỷ",
  "report.sign":         "Ký + gửi",
  "report.sending":      "Đang ký…",
  "report.ok":           "Đã gửi báo cáo",
  "report.fail":         "Thất bại",

  "auth.intro":   "Đăng nhập bằng tài khoản bệnh viện để tiếp tục.",
  "auth.signin":  "Đăng nhập bằng SSO",
};

const LOCALES: Record<Locale, typeof en> = { en, vi };

const detect = (): Locale => {
  if (typeof navigator === "undefined") return "en";
  const pref = (navigator.language || "en").toLowerCase();
  if (pref.startsWith("vi")) return "vi";
  return "en";
};

const [locale, setLocale] = createSignal<Locale>(detect());

export { locale, setLocale };

export function t(key: keyof typeof en): string {
  return LOCALES[locale()][key] ?? key;
}
